import { Entity } from '../common/queryClient';
import {
  CategoryGroupSchema,
  ClassifierGroupSchema,
} from '../classifier/types';
import { z } from 'zod/v3';
import { objectWithRandomUniqueKey } from '../common/utils/randomUniqueKey';
import {
  DataObjectDirectoryDto,
  DataObjectFormDataSchema,
  DataObjectMutationDtoSchema,
  IndicatorFormDataSchema,
  VersionDataSchema,
} from './schema';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './translations/validation.translations.json';
import { DataObjectGroupDataDto, DataObjectGroupDto } from '../dataObjectGroup';
import {
  DataObjectRelationEdgeSchema,
  DataObjectRelationNodeSchema,
  DataObjectRelationsGraphDtoSchema,
} from './schema/DataObjectRelationsGraphDtoSchema';
import {
  DataObjectRelationsTableDtoSchema,
  DataObjectRelationsTableRowDtoSchema,
} from './schema/DataObjectRelationsTableDtoSchema';
import {
  DataObjectRelationVersionSchema,
  DataObjectVersionSchema,
  DataObjectVersionSourceSchema,
} from './schema/DataObjectVersionSchema';
import {
  zCode,
  zCodeWithoutLeadingNumber,
  zDescription,
  zName,
} from '../common/utils/schema/common';
import { errorMap } from '../common/utils/schema/errorMap';
import { SystemClassifierItemSchema } from '../systemClassifier/schema/SystemClassifierItemSchema';

const { addTranslations, t } = i18nNsFactory('data-object-types');
addTranslations(translations);

export const EXCEL_WIN_FILENAME_REGEX = /^(((\\+)[^\\/]+)+).xlsx$/;
export const EXCEL_POSIX_FILENAME_REGEX = /^(((\/+)[^\\/]+)+).xlsx$/;
export const EXCEL_FILENAME_REGEX = new RegExp(
  EXCEL_WIN_FILENAME_REGEX.source + '|' + EXCEL_POSIX_FILENAME_REGEX.source,
);

export const dataObjectContentSegmentKeys = [
  'dataSources',
  'dimensions',
  'indicators',
  'validationRules',
  'algorithms',
  'requiredKeys',
  'execution',
  'executionDeadlines',
  'alternativeAlgorithmSets',
] as const;

export type DataObjectContentSegmentKey =
  (typeof dataObjectContentSegmentKeys)[number];

export interface ActivePeriod {
  versionId: number;
  validFrom: string | null;
  validTo: string | null;
}

export interface DataObjectMiniDto extends Entity {
  code: string;
  name: string;
  description: string | null;
  dataObjectType: DataObjectType;
  dataObjectGroupId: number;
  groupType: DataObjectGroupType;
  dataObjectGroupData: DataObjectGroupDataDto | null;
  outputTypeId?: number;
  outputTypeCode?: string;
  activePeriods: ActivePeriod[] | null;
  hasComments: boolean;
  hasAccess: boolean;
  userFollowedGroupPeriodSkipCount?: null;
}

export enum DataObjectType {
  IN = 'IN',
  OUT = 'OUT',
}

export enum DataObjectOutputType {
  OUTPUT = 'OUTPUT',
  REPORT = 'REPORT', //Report
  INTERMEDIATE = 'INTERMEDIATE', //Vaheväljund
}

export enum DataObjectFormType {
  IN = 'IN',
  OUTPUT = 'OUTPUT',
  REPORT = 'REPORT', //Report
  INTERMEDIATE = 'INTERMEDIATE', //Vaheväljund
}

export const dataObjectDescriptionMapping = {
  INPUT: {
    dataObjectType: DataObjectType.IN,
    outputType: undefined,
  },
  OUTPUT: {
    dataObjectType: DataObjectType.OUT,
    outputType: DataObjectOutputType.OUTPUT,
  },
  INTERMEDIATE: {
    dataObjectType: DataObjectType.OUT,
    outputType: DataObjectOutputType.INTERMEDIATE,
  },
  REPORT: {
    dataObjectType: DataObjectType.OUT,
    outputType: DataObjectOutputType.REPORT,
  },
};

export enum DataObjectRelationNodeType {
  SINGLE = 'SINGLE',
  GROUP = 'GROUP',
}

export enum DataObjectRelationEdgeType {
  ALGO = 'ALGO',
  DATA = 'DATA',
}

export enum RelationsTableType {
  OUTGOING = 'OUTGOING',
  INCOMING = 'INCOMING',
  INTERNAL = 'INTERNAL',
}

export enum AlgorithmElementFunctionViewType {
  F = 'F', //filter
  T = 'T', //tingimus
  R = 'R', //arvutusreegel
}

export enum AlgorithmElementFunctionType {
  FEATURE = 'FEATURE', //tunnus
  FUNCTION = 'FUNCTION', //funktsioon
  OPERATOR = 'OPERATOR', //operaator
}

export enum AlgorithmTargetRuleType {
  MAP = 'MAP', //Mäppingud
  RULE = 'RULE', //Arvutusreeglid
}

export enum DataObjectDimensionType {
  SUBJECT = 'SUBJECT', //Subjekt
  SUBJECT_ATTRIBUTE = 'SUBJECT_ATTRIBUTE', //Subjekti tunnus
  CLASSIFIER = 'CLASSIFIER', //Klassifikaator
  CONSTANT = 'CONSTANT', //Konstantne
  KEY_BASED = 'KEY_BASED', //Võtmepõhine
}

export enum RelationsSearchGroupByType {
  INPUTS = 'INPUTS', //Sisendid
  OUTPUTS = 'OUTPUTS', //Väljundid
  SELECTED = 'SELECTED', //Valitud
}

export type Frequency =
  | 'YEAR'
  | 'HALFYEAR'
  | 'QUARTER'
  | 'MONTH'
  | 'WEEK'
  | 'DAY';

export const isDataObjectType = (value: unknown): value is DataObjectType =>
  Boolean(value) &&
  Object.values(DataObjectType).includes(value as DataObjectType);

export type DataObjectDirectorySortField = keyof DataObjectDirectoryDto;

export const DataObjectGroupType = {
  I: 'I',
  O: 'O',
} as const;

export type DataObjectGroupType =
  (typeof DataObjectGroupType)[keyof typeof DataObjectGroupType];

export const isDataObjectGroupType = (
  value: unknown,
): value is DataObjectGroupType =>
  Boolean(value) &&
  Object.values(DataObjectGroupType).includes(value as DataObjectGroupType);

export class DataObjectView {
  static readonly IN = new DataObjectView(
    'IN',
    DataObjectType.IN,
    DataObjectGroupType.I,
  );
  static readonly OUT = new DataObjectView(
    'OUT',
    DataObjectType.OUT,
    DataObjectGroupType.O,
  );
  private constructor(
    public readonly key: string,
    public readonly dataObjectType: DataObjectType,
    public readonly dataObjectGroupType: DataObjectGroupType,
  ) {}

  get isInput() {
    return this.dataObjectType === DataObjectType.IN;
  }

  get isOutput() {
    return this.dataObjectType === DataObjectType.IN;
  }
}

export type DataObjectCloneEntityType = 'description' | 'version';

export interface DataObjectGroupWithDescriptionsDto
  extends Pick<
    DataObjectGroupDto,
    'id' | 'code' | 'name' | 'groupType' | 'dataObjectGroupData'
  > {
  isSystemic: boolean;
  userFollowedGroupPeriodSkipCount?: number;
  descriptions: DataObjectMiniDto[];
}

export interface TreeDataChildrenDto {
  value: string | number;
  title: string;
  children?: TreeDataChildrenDto[];
}

export type DataObjectFilterDto = {
  confidentialityStatusIds?: number[];
  dataObjectType?: string;
  dataProviderGroupIds?: number[];
  domainIds?: number[];
  frequencyIds?: number[];
  groupIds?: number[];
  accessGroupIds?: number[];
  indicatorStatus?: false | true;
  originIds?: number[];
  outputTypeCodes?: string[];
  outputs?: number[];
  primaryOutputIds?: number[];
  recipientIds?: number[];
  search?: string;
  sourceIds?: number[];
  statusCodes?: string[];
  subgroupIds?: number[];
  validFrom?: string;
  validTo?: string;
};

export type DataObjectRelationNode = z.input<
  typeof DataObjectRelationNodeSchema
>;
export type DataObjectRelationEdge = z.input<
  typeof DataObjectRelationEdgeSchema
>;
export type DataObjectRelationsGraphDto = z.input<
  typeof DataObjectRelationsGraphDtoSchema
>;
export type DataObjectRelationsTableRowDto = z.input<
  typeof DataObjectRelationsTableRowDtoSchema
>;
export type DataObjectRelationsTableDto = z.input<
  typeof DataObjectRelationsTableDtoSchema
>;

export type DataObjectQueryDtoType = z.input<typeof DataObjectFormDataSchema>;
export type DataObjectFormDataType = z.infer<typeof DataObjectFormDataSchema>;

export type VersionQueryDtoType = z.input<typeof VersionDataSchema>;
export type VersionDataType = z.infer<typeof VersionDataSchema>;

export type DataObjectVersionType = z.infer<typeof DataObjectVersionSchema>;
export type DataObjectRelationVersionType = z.infer<
  typeof DataObjectRelationVersionSchema
>;
export type DataObjectVersionSourceType = z.infer<
  typeof DataObjectVersionSourceSchema
>;

export type IndicatorFormDataType = z.infer<typeof IndicatorFormDataSchema>;

export type DataObjectMutationDtoType = z.infer<
  typeof DataObjectMutationDtoSchema
>;

export const DimensionFormDataSchema = z
  .object({
    id: z.number().nullish(),
    code: zCodeWithoutLeadingNumber(),
    name: zName(errorMap(() => t('validation.dimension_name_required'))).min(1),
    description: zDescription().nullable(),
    width: z
      .number(errorMap(() => t('validation.field_required')))
      .positive()
      .max(999),
    sequenceNumber: z.number().nullable(),
    classifier: ClassifierGroupSchema.nullable(),
    allCategories: z.boolean().nullable(),
    category: CategoryGroupSchema.nullable(),
    levelNumber: z.number().nullable(),
    constantValue: z.string().nullable(),
    required: z.boolean().nullish(),
    aggregateHierarchy: z.boolean().nullable(),
    dimensionType: z.lazy(() => SystemClassifierItemSchema).nullable(),
  })
  .transform(
    ({ classifier, allCategories, category, levelNumber, ...rest }) => ({
      classifier: classifier && {
        classifier,
        allCategories,
        category,
        levelNumber,
      },
      ...rest,
    }),
  );

const DimensionClassifierFormDataSchema = z.object({
  classifier: ClassifierGroupSchema,
  allCategories: z.boolean().nullable(),
  category: CategoryGroupSchema.nullable(),
  levelNumber: z.number().nullable(),
});

export const DimensionValidFormDataSchema = DimensionFormDataSchema.innerType()
  .omit({
    classifier: true,
    allCategories: true,
    category: true,
    levelNumber: true,
  })
  .extend({
    classifier: DimensionClassifierFormDataSchema.nullable(),
  });

export type DimensionFormDataType = z.infer<typeof DimensionFormDataSchema>;
export type DimensionValidFormDataType = z.infer<
  typeof DimensionValidFormDataSchema
>;

export type DimensionClassifierFormDataType = z.infer<
  typeof DimensionClassifierFormDataSchema
>;

export const locationSourceTypes = ['FILE', 'URL'] as const;
export const sourceTypes = ['DATABASE', ...locationSourceTypes] as const;

export const dataSourceErrorMap = (issue: z.ZodIssueOptionalMessage) => {
  if (issue.code === z.ZodIssueCode.invalid_union_discriminator) {
    return t('validation.field_required');
  }
  if (issue.code === z.ZodIssueCode.too_big) {
    return t('validation.too_big', { max: issue.maximum });
  }
  if (
    issue.code === z.ZodIssueCode.invalid_string &&
    issue.validation === 'regex'
  ) {
    return t('validation.invalid_excel_path');
  }
  if (
    issue.code === z.ZodIssueCode.invalid_string &&
    issue.validation === 'url'
  ) {
    return t('validation.invalid_url');
  }
  return t('validation.field_required');
};

export const DataSourceSchema = z.object({
  id: z.number().nullable(),
  code: z.string().nullable(),
  name: zName().nullable(),
  description: zDescription().nullable(),
  sourceType: z.enum(sourceTypes).or(z.null()),
  groupSource: z.boolean(),
  dataLocation: z.string(errorMap(dataSourceErrorMap)).min(1).max(1000),
  connectionId: z.string(errorMap(dataSourceErrorMap)).min(1),
  valueDateColumnName: z.string().nullable(),
  dataObjectCodeColumnName: z.string().nullable(),
  subjectCodeColumnName: z.string().nullable(),
});

export const DatabaseSourceSchema = DataSourceSchema.extend({
  sourceType: z.literal('DATABASE'),
  code: zCode(),
  name: zName(errorMap(dataSourceErrorMap)).min(1),
  dataLocation: z.string(errorMap(dataSourceErrorMap)).min(1).max(1000),
  valueDateColumnName: z.string(errorMap(dataSourceErrorMap)).max(255),
  dataObjectCodeColumnName: z
    .string(errorMap(dataSourceErrorMap))
    .max(255)
    .nullable(),
  subjectCodeColumnName: z.string(errorMap(dataSourceErrorMap)).max(255),
});

export const LocationSourceSchema = DataSourceSchema.extend({
  sourceType: z.literal('FILE'),
  dataLocation: z
    .string(errorMap(dataSourceErrorMap))
    .regex(EXCEL_FILENAME_REGEX),
});

export const URLSourceSchema = DataSourceSchema.extend({
  sourceType: z.literal('URL'),
  dataLocation: z.string(errorMap(dataSourceErrorMap)).url(),
  methodNameId: z.number(errorMap(dataSourceErrorMap)),
  contentType: z.string().nullish(),
  contentBody: z.string().nullish(),
});

export type SourceType = (typeof sourceTypes)[number];

export const DataSourceFormDataSchema = z
  .discriminatedUnion(
    'sourceType',
    [DatabaseSourceSchema, LocationSourceSchema, URLSourceSchema],
    errorMap(dataSourceErrorMap),
  )
  .superRefine((data, ctx) => {
    if (
      data.sourceType === 'DATABASE' &&
      data.groupSource &&
      !data.dataObjectCodeColumnName
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.field_required'),
        path: ['dataObjectCodeColumnName'],
      });
    }
  })
  .transform(objectWithRandomUniqueKey);
